import { Component, inject } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { AuthButtonComponent } from '@processpuzzle/auth';
import { AppShellComponent, PublishedAppStore, ShellActionsDirective } from '@processpuzzle/base-app';
import { DesignButtonComponent, LanguageSelectorComponent } from '@processpuzzle/base-widget';
import { ORG_ADMIN_ORG_KEY } from '@processpuzzle/org-admin';
import { OrganizationSwitcherComponent } from '../navigation/organization-switcher/organization-switcher.component';

/**
 * The organization's application as its designer last **published** it — theme, layout, regions,
 * navigation and screens all from the definition, rendered by the same `AppShellComponent` the
 * designer's Preview renders the draft with. So what the Preview shows is what Publish puts here.
 *
 * The routes come from `publishedApplicationGuard` on this component's route, which fetches the
 * published revision whenever the application is entered — coming back from the designer included —
 * and leaves the "not published yet" page in their place until there is one.
 *
 * **The platform's controls are this host's, not the definition's.** Signing in and out, switching
 * organization, the designer and the users are projected into the end of the shell's top row, so they
 * are there whatever the definition's header holds — a freshly seeded application has no header at
 * all, and a designer must not be able to lock themselves out of the designer.
 */
@Component({
  selector: 'app-runtime-host',
  imports: [
    AppShellComponent,
    AuthButtonComponent,
    DesignButtonComponent,
    LanguageSelectorComponent,
    MatIcon,
    MatIconButton,
    MatTooltip,
    OrganizationSwitcherComponent,
    RouterLink,
    ShellActionsDirective,
    TranslocoDirective,
  ],
  template: `
    <pp-app-shell [definition]="store.published()">
      <div ppShellActions>
        <pp-language-selector />
        <app-organization-switcher />
        <ng-container *transloco="let t; prefix: 'navigation'">
          <a mat-icon-button [routerLink]="['/', orgKey, 'admin']" [matTooltip]="t('admin.users')" [attr.aria-label]="t('admin.users')" test-id="runtime-users">
            <mat-icon>group</mat-icon>
          </a>
        </ng-container>
        <pp-design-button />
        <pp-auth-button />
      </div>
    </pp-app-shell>
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
    }
  `,
})
export class RuntimeHostComponent {
  protected readonly store = inject(PublishedAppStore);
  protected readonly orgKey = inject(ORG_ADMIN_ORG_KEY);
}
